<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Relatório de Contatos</v-toolbar-title>
                    <v-spacer/>
                    <PeriodoFilter
                        v-model="periodo"
                        @update="consultar"
                    />
                </v-toolbar>

                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <v-row>
                            <!-- Ramos -->
                            <v-col cols="12" sm="6" md="6">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Ramos
                                    </div>
                                    <canvas id="ramos" :height="pieChartHeight"/>
                                </v-card>
                            </v-col>

                            <!-- Pedidos -->
                            <v-col cols="12" sm="6" md="6">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Pedidos
                                    </div>
                                    <canvas id="pedidos" :height="pieChartHeight"/>
                                </v-card>
                            </v-col>

                            <!-- Score -->
                            <v-col cols="12" sm="6" md="4">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Faixa de Score
                                    </div>
                                    <canvas id="score" :height="pieChartHeight"/>
                                </v-card>
                            </v-col>

                            <!-- Faturamento -->
                            <v-col cols="12" sm="6" md="4">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Faturamento
                                    </div>
                                    <canvas id="faturamento" :height="pieChartHeight"/>
                                </v-card>
                            </v-col>

                            <!-- Tempo de Empresa -->
                            <v-col cols="12" sm="6" md="4">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Tempo de Empresa
                                    </div>
                                    <canvas id="tempo_empresa" :height="pieChartHeight"/>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import PeriodoFilter from '@/components/PeriodoFilter';
import { getColors } from '@/utils/colors';
import Chart from 'chart.js';

export default {
    name: 'RelatorioContatos',

    components: {
        BaseContainer,
        PeriodoFilter,
    },

    data: () => ({
        loading: false,
        dados: {},
        periodo: {},
        pieCharts: {},
        chartKeys: ['ramos', 'pedidos', 'score', 'faturamento', 'tempo_empresa'],
    }),

    mounted() {
        this.mountCharts();
        this.consultar();
    },

    computed: {
        pieChartHeight() {
            const w = this.$vuetify.breakpoint.width;
            if (w <= 576) return '400';
            if (w > 576 && w <= 768) return '300';
            if (w > 768 && w <= 992) return '250';
            if (w > 992) return '200';
            return '200';
        },
    },

    methods: {
        consultar() {
            const start = this.moment(this.periodo.start).format('YYYY-MM-DD HH:mm');
            const end = this.moment(this.periodo.end).format('YYYY-MM-DD HH:mm');

            const filter = `start=${start}&end=${end}`;

            this.loading = true;
            this.$http.post(`contatos/estatisticas?${filter}`).then(resp => {
                this.dados = resp.data.data || {};
                this.gerarGraficos();
            }).catch(e => {
                console.log(e);
            }).finally(() => (this.loading = false));
        },

        gerarGraficos() {
            const ramos = (this.dados.ramos || []).filter(e => e.ramo);
            const pedidos = (this.dados.pedidos || []).filter(e => e.pedidos);
            const score = (this.dados.score || []).filter(e => e.score);
            const faturamento = (this.dados.faturamento || []).filter(e => e.faturamento);
            const tempoEmpresa = (this.dados.tempo_empresa || []).filter(e => e.tempo_empresa);

            this.updatePie('ramos', ramos, 'ramo', 'total');
            this.updatePie('pedidos', pedidos, 'pedidos', 'total');
            this.updatePie('score', score, 'score', 'total');
            this.updatePie('faturamento', faturamento, 'faturamento', 'total');
            this.updatePie('tempo_empresa', tempoEmpresa, 'tempo_empresa', 'total');
        },

        updatePie(key, rows, labelField, valueField) {
            if (!this.pieCharts[key]) return;

            this.pieCharts[key].data = {
                labels: rows.map(p => p[labelField]),
                datasets: [
                    {
                        data: rows.map(p => p[valueField]),
                        backgroundColor: this.getColors(),
                    }
                ]
            };

            this.pieCharts[key].update();
        },

        mountCharts() {
            const common = { type: 'pie', data: { labels: [], datasets: [] } };
            const $ = (el) => document.getElementById(el);

            this.chartKeys.forEach((key) => {
                const el = $(key);
                if (!el) return;

                this.pieCharts[key] = new Chart(el, {
                    ...common,
                    options: this.getPieChartOptions()
                });
            });
        },

        getColors() {
            return getColors();
        },

        getPieChartOptions(formatPrice = false) {
            return {
                responsive: true,
                maintainAspectRatio: true,
                legend: {
                    position: this.$vuetify.breakpoint.mdAndDown ? 'top' : 'right',
                },
                tooltips: {
                    callbacks: {
                        label: (tooltipItem, data) => {
                            const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                            const label = data.labels[tooltipItem.index];
                            if (formatPrice) {
                                return 'R$ ' + this.formatPrice(value);
                            }
                            return label + ' - ' + value;
                        }
                    }
                }
            };
        },
    },
};
</script>
